import { SUPPORTED_COUNTRIES } from "@micropod/i18n";
import { type FormEvent, useState } from "react";
import { api, getToken } from "../lib/api";
import { createOrganization, setActiveOrganization } from "../lib/api/auth";

/**
 * The workspace IS the tenant.
 *
 * Better Auth's organization already models what we needed, so `activeOrganizationId`
 * becomes `ctx.tenantId` on the server and there is no parallel tenancy to keep in sync.
 * See docs/07-stack-2026.md §3.
 */
export function CreateWorkspace({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState("");
  const [country, setCountry] = useState(guessCountry);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const slug = name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

  async function submit(e: FormEvent) {
    e.preventDefault();
    const token = getToken();
    if (!token) return;
    setError(null);
    setBusy(true);
    try {
      const org = await createOrganization(token, { name: name.trim(), slug, country });
      await setActiveOrganization(token, org.id);

      // Better Auth drops fields it does not know, so `country` never reaches the server
      // through organization/create — it has to be sent here. Until this runs the tenant
      // has an identity but no shape: no currency, no clock, and no answer to "which
      // database?". Region is derived server-side and fixed for the life of the account.
      await api.tenant.provision.mutate({
        name: name.trim(),
        country,
        // The browser knows its own IANA zone exactly; a country table only guesses.
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });

      onDone();
    } catch (err) {
      setError(
        err instanceof Error && /slug/i.test(err.message)
          ? "That name is already taken. Try a slightly different one."
          : "We could not create the workspace. Try again in a moment.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-form">
      <h1 className="signin-title">Name your business</h1>
      <p className="signin-sub">
        This is how your team will see it, and it is what your WhatsApp number will be connected to.
      </p>

      <form className="fields" onSubmit={submit}>
        <label className="field">
          <span>Business name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Bright Futures Coaching"
            autoComplete="organization"
            required
          />
          {slug && <small className="mono">micropod.io/{slug}</small>}
        </label>

        {/* One question, pre-filled from the browser's own timezone. It decides the
              currency on every invoice and which database the data lives in for the life
              of the account — a D1 jurisdiction is fixed at creation — so it is asked
              once, here, rather than discovered later. */}
        <label className="field">
          <span>Where do you operate?</span>
          <select value={country} onChange={(e) => setCountry(e.target.value)} required>
            {SUPPORTED_COUNTRIES.map((cc) => (
              <option key={cc} value={cc}>
                {countryName(cc)}
              </option>
            ))}
          </select>
          <small>
            Sets your currency and where your data is stored. It cannot be changed later.
          </small>
        </label>

        {error && (
          <p className="formerror" role="alert">
            {error}
          </p>
        )}

        <button type="submit" className="btn primary" disabled={busy || slug.length < 2}>
          {busy ? "Creating…" : "Continue"}
        </button>
      </form>
    </div>
  );
}

/** The browser knows its zone exactly; the country is an inference from it, so it is a
 *  starting point the owner can correct rather than an answer. */
function guessCountry(): string {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const guess = ZONE_COUNTRY[zone];
    if (guess && SUPPORTED_COUNTRIES.includes(guess)) return guess;
  } catch {
    // A locked-down browser gets the default below, which they can change.
  }
  return "IN";
}

/** Just enough to pre-select the right row for the markets we launch into. */
const ZONE_COUNTRY: Record<string, string> = {
  "Asia/Kolkata": "IN",
  "Asia/Calcutta": "IN",
  "Asia/Jakarta": "ID",
  "Asia/Karachi": "PK",
  "Asia/Dhaka": "BD",
  "Asia/Manila": "PH",
  "Asia/Kuala_Lumpur": "MY",
  "Asia/Singapore": "SG",
  "Asia/Dubai": "AE",
  "Africa/Lagos": "NG",
  "Africa/Johannesburg": "ZA",
  "America/Sao_Paulo": "BR",
  "America/Mexico_City": "MX",
  "Europe/London": "GB",
  "Europe/Dublin": "IE",
  "Europe/Berlin": "DE",
  "Europe/Paris": "FR",
  "Europe/Madrid": "ES",
  "Europe/Rome": "IT",
  "Europe/Amsterdam": "NL",
  "Australia/Sydney": "AU",
  "America/New_York": "US",
  "America/Chicago": "US",
  "America/Denver": "US",
  "America/Los_Angeles": "US",
  "America/Toronto": "CA",
  "America/Vancouver": "CA",
};

const REGION_NAMES = new Intl.DisplayNames(undefined, { type: "region" });

function countryName(cc: string): string {
  try {
    return REGION_NAMES.of(cc) ?? cc;
  } catch {
    return cc;
  }
}
